import React, { useContext } from 'react';

import { type Island, isTopModelSku } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DIMENSION_FONT_SIZE } from '@r-core/constants';
import { DisplayMode } from '@r-core';
import { getUprightTextCorrection } from '../helpers/get-upright-text-correction';

export type RenderCountertopTopModelLabelsProps = {
	island: Island;
};

// Top-model fixtures (e.g. the kegerator tap) carry SVG dimension wording that
// must sit above every other appliance cutout. SVG paints in document order, so
// we re-render just their `svg.item` overlay here -- after all panel cutouts and
// dimensions -- using the same panel/group/item transform nesting as the inline
// render, so the wording lands on top without moving relative to its fixture.
export const RenderCountertopTopModelLabels = (props: RenderCountertopTopModelLabelsProps): React.ReactElement => {
	const { island } = props;
	const { controller: { options, navigation } } = useContext(AppContext);
	const fontSize = DIMENSION_FONT_SIZE * options.dimensions.fontSizeFactor;

	// The dimension wording only belongs in manufacturing views. In hybrid views the
	// white cutout is still drawn by the panel-item render pass, so suppressing this
	// overlay leaves just the cutout without the surrounding text/notes.
	const isManufacturingView = navigation.displayMode === DisplayMode.Manufacturing
		|| navigation.displayMode === DisplayMode.InlineManufacturing;
	if (!isManufacturingView) return <></>;

	return <>
		{island.panels.children.map((panel) => (
			<g key={`tm-${panel.id}`} transform={`translate(${panel.center.x}, ${panel.center.z}) rotate(${-panel.getOriginAngle()})`}>
				{panel.panelItemGroups.children.map((group) => (
					<g key={`tm-${group.id}`} transform={`translate(${group.center.x}, ${group.center.z})`}>
						{group.items.children
							// Off-panel-detail SKUs (e.g. the kegerator tap, GitHub #282/#385) render their
							// wording via RenderCountertopCutoutDetailBlocks instead -- never both places.
							.filter((panelItem) => isTopModelSku(panelItem.item.sku) && panelItem.boundTop && !panelItem.item.component.svg?.offPanelDetail)
							.map((panelItem) => {
								const { z: irZ } = panelItem.effectiveRotation;
								const overheadRotationDeg = -irZ * (180 / Math.PI);
								const uprightCorrection = getUprightTextCorrection(-panel.getOriginAngle() + overheadRotationDeg);
								return <g key={`tm-${panelItem.id}`} transform={`translate(${panelItem.center.x}, ${panelItem.center.z})${overheadRotationDeg !== 0 ? ` rotate(${overheadRotationDeg})` : ''}`}>
									<g transform={uprightCorrection !== 0 ? `rotate(${uprightCorrection})` : undefined}>
										{panelItem.item.component.svg?.item?.({ diameter: panelItem.item.cutout.width, fontSize })}
									</g>
								</g>;
							})}
					</g>
				))}
			</g>
		))}
	</>;
};
